import { useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

// LocalStorage keys.
// `analysis_id` is the SERVER-issued session ID (created lazily after the
// first successful analysis). `client_session_id` is a client-only UUID we
// generate on first use — it lives forever in the browser and lets the
// backend deduplicate re-runs of the same property from the same visitor.
const LS_CLIENT_SESSION_ID = "rd_client_session_id";
const LS_ANALYSIS_ID = "rd_analysis_id";

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers / test environments
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function _readLS(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function _writeLS(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Safari private mode etc. — silent */
  }
}

function _getClientSessionId() {
  let id = _readLS(LS_CLIENT_SESSION_ID);
  if (!id) {
    id = _uuid();
    _writeLS(LS_CLIENT_SESSION_ID, id);
  }
  return id;
}

/**
 * useAnalysisSession — the funnel plumbing hook.
 *
 * Two responsibilities:
 *   1. Manage the anonymous `analysis_id` that ties every funnel event
 *      (view → email capture → tripwire → subscribe) back to the same
 *      property + same visitor. Persisted in localStorage so it survives
 *      refresh & navigation.
 *   2. Provide a `trackFunnelEvent(stage, metadata)` helper the rest of
 *      the app calls at each funnel milestone. Silently no-ops if
 *      analytics is unreachable — never blocks the UI.
 *
 * The `registerAnalysis` call is what MINTS the analysis_id on the server.
 * Call it exactly once per completed analysis (idempotency is enforced
 * server-side via client_session_id + property_id + 24h window).
 *
 * Usage:
 *   const { analysisId, registerAnalysis, trackFunnelEvent } = useAnalysisSession();
 *   // when user clicks Analyze:
 *   trackFunnelEvent("analysis_started", { property_id });
 *   // after result renders:
 *   const id = await registerAnalysis({ property_id, property_snapshot, analysis_params, analysis_result_summary });
 */
export function useAnalysisSession() {
  // Use a ref instead of state — this is a fire-and-forget correlation ID,
  // NOT something the UI should re-render on. useState would cause every
  // consumer to re-render whenever the session changes, which is wasteful
  // for a value nothing in the visual tree actually depends on.
  const analysisIdRef = useRef(_readLS(LS_ANALYSIS_ID));

  const registerAnalysis = useCallback(async ({
    property_id,
    property_snapshot = {},
    analysis_params = {},
    analysis_result_summary = {},
  }) => {
    if (!property_id) return null;
    const clientSessionId = _getClientSessionId();
    try {
      const utm = _readUtmFromLocation();
      const { data } = await axios.post(`${API}/funnel/analysis-session`, {
        property_id,
        property_snapshot,
        analysis_params,
        analysis_result_summary,
        client_session_id: clientSessionId,
        referrer: document.referrer || "",
        ...utm,
      });
      const id = data?.analysis_id;
      if (id) {
        analysisIdRef.current = id;
        _writeLS(LS_ANALYSIS_ID, id);
      }
      return id;
    } catch {
      // Analytics is best-effort. A failure here must not block the user
      // from actually SEEING their analysis result.
      return null;
    }
  }, []);

  const trackFunnelEvent = useCallback(async (stage, metadata = {}) => {
    // `analysis_started` fires before the session exists — pass a
    // pending sentinel so the server-side validator lets it through.
    const analysisId = analysisIdRef.current || "pending";
    try {
      await axios.post(`${API}/funnel/event`, {
        analysis_id: analysisId,
        event_type: stage,
        metadata: metadata || {},
      });
    } catch {
      /* silent — funnel events are best-effort */
    }
  }, []);

  return {
    analysisId: analysisIdRef.current,
    registerAnalysis,
    trackFunnelEvent,
    getClientSessionId: _getClientSessionId,
  };
}

// Parse UTM params from the current URL. Attribution stays crisp only when
// captured at the moment of first analysis — otherwise SPA navigation
// scrubs them and we lose the acquisition-source signal.
function _readUtmFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
    };
  } catch {
    return {};
  }
}
